import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { publicConfig } from 'config'
import { discussionsApi, isSignInError, type MediaUpload } from 'lib/community/discussions-api'
import {
  DISCUSSION_KINDS,
  DISCUSSION_LIMITS,
  KIND_FIXED_LINE,
  SAVED_FLASH_MS,
  applyPreset,
  canAddBlock,
  canPost,
  categoryFromGear,
  topCategories,
  checklist,
  draftStorage,
  expiredPhotos,
  expiredPhotosLine,
  fileRefusal,
  freshDraft,
  imageRoom,
  isBlankDraft,
  isCover,
  isEmptyPatch,
  kindCopy,
  postBlocker,
  postedRedirect,
  editHasChanges,
  restoredDraftLine,
  savedNoticeStorage,
  skippedFilesLine,
  toCreateBody,
  toPatchBody,
  uploadsPending,
  writerReducer,
  type DraftBlock,
  type DraftBlockType,
  type DraftGear,
  type DraftImage,
  type WriterAction,
  type WriterDraft,
  type WriterPreset,
} from 'lib/community/discussions-writer'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { discussionsHref } from 'lib/community/discussions'
import { DRAFT_COPY, draftHomeHref, writerSavedStatus } from 'lib/community/draft-card'
import {
  WRITER_PHONE_QUERY,
  caretUnderBar,
  closeGoesBack,
  flushDraftOnHide,
  phoneDraftStatus,
  postDisabledReason,
} from 'lib/community/writer-phone'
import type { CommunityCategoryNode } from 'lib/community/types'
import { OwnerApiError } from 'lib/owner-notes/api'
import {
  useOwnerCredentialOptional,
  useOwnerSessionOptional,
  type OwnerCredential,
} from 'lib/owner-notes/auth-bridge'
import Crumbs, { type Crumb } from 'components/community/Crumbs'
import base from 'components/community/community.module.css'
import CategoryPanel from './CategoryPanel'
import Checklist from './Checklist'
import GearPanel from './GearPanel'
import HeadingBlock from './HeadingBlock'
import ImagesBlock from './ImagesBlock'
import { LeaveDialog, useLeaveGuard } from './LeaveGuard'
import LinkBlock from './LinkBlock'
import PhoneChecklist from './PhoneChecklist'
import PhonePickers from './PhonePickers'
import PhoneToolbar from './PhoneToolbar'
import ReorderList from './ReorderList'
import TextBlock from './TextBlock'
import styles from './Writer.module.css'
import { useViewportInsets } from './useViewportInsets'

interface WriterProps {
  crumbs: Crumb[]
  /** DealHunt's category list (every node under audio); empty → no category card. The top-level
   *  ones are the pills and what a linked product picks; a picked one shows its children. */
  categories: CommunityCategoryNode[]
  /** `?kind=`, `?product=`, `?category=` and `?title=`, validated by the server. */
  preset: WriterPreset
  /** Editing a posted discussion instead of writing a new one (`/community/discussions/edit/<id>`). */
  edit?: WriterEdit
}

/**
 * The edit view's input: the author's own post as a draft (`draftFromDiscussion` of `/mine`).
 * Editing never touches this browser's stored NEW-post draft (`draftStorage`): no restore, no
 * autosave, no Save draft — the post itself is the saved copy.
 */
export interface WriterEdit {
  id: string
  initial: WriterDraft
  /** Answered already: gear is shown, not editable (`gearLockedFor`). */
  gearLocked: boolean
  /** The thread's address — Cancel, and where a save goes if DealHunt's url is unusable. */
  threadHref: string
}

/** Typing pause before the draft is written to this browser. */
const AUTOSAVE_MS = 1500
/** Photos uploading at once. */
const UPLOAD_SLOTS = 3

type SaveState = 'idle' | 'saved' | 'failed'

const ADD_BUTTONS: { type: DraftBlockType; glyph: string; label: string }[] = [
  { type: 'text', glyph: '¶', label: 'Text' },
  { type: 'heading', glyph: 'H', label: 'Heading' },
  { type: 'images', glyph: '▦', label: 'Photos' },
  { type: 'link', glyph: '🔗', label: 'Link' },
]

function newImageKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/** A session the proxy has not refused: automatic retries never re-send a refused token. */
function usable(cred: OwnerCredential | null, rejected: string | null): cred is OwnerCredential {
  return Boolean(cred && cred.token !== rejected)
}

/** The block that holds the photo `key`, or null once it is gone. */
function imageBlockId(draft: WriterDraft, key: string): number | null {
  const block = draft.blocks.find(b => b.type === 'images' && b.images.some(im => im.key === key))
  return block ? block.id : null
}

/**
 * The discussions writer (design 10b). Every rule is `lib/community/discussions-writer`'s; this
 * holds the draft, the uploads and the post.
 *
 * The server renders the start state (one empty text block, the link's preset) and nothing of
 * the reader; the stored draft loads in an effect. Autosave writes it to this browser
 * `AUTOSAVE_MS` after a change and on every blur — "Draft saved" is that local save (no server
 * drafts in v1). Posting and uploading need a session: without one, the in-page sign-in dialog
 * opens and the action runs once the session arrives; the draft is already stored.
 */
const Writer: React.VFC<WriterProps> = ({ crumbs, categories, preset, edit }) => {
  const pills = useMemo(() => topCategories(categories), [categories])
  const router = useRouter()
  const { track } = useCatalogEvent()
  const { showLogIn } = useOwnerSessionOptional()
  const credential = useOwnerCredentialOptional()
  const credentialRef = useRef<OwnerCredential | null>(credential)
  credentialRef.current = credential
  const token = credential?.token ?? null
  // A token the proxy refused (401: a lapsed session the browser still holds). The automatic
  // retries — queued photos, a post waiting for sign-in — wait for a DIFFERENT token, or each
  // refusal would re-queue, re-send and re-open the dialog in a loop.
  const rejectedTokenRef = useRef<string | null>(null)

  const [draft, rawDispatch] = useReducer(writerReducer, undefined, () => {
    if (edit) {
      return edit.initial
    }
    return freshDraft(preset, categories)
  })
  // The edit view keeps no local draft: every path to `draftStorage` checks this first, so the
  // reader's unfinished NEW post (and the rail's "Continue writing") is never overwritten.
  const localDraft = !edit
  // A change since the last save (the stored draft's own load is none): autosave only writes after
  // the reader acts, so arriving on the page never claims "Draft saved", and Save draft is
  // disabled while there is nothing new to save. The ref is for the save paths, the state for
  // the button; a save that SUCCEEDS clears both (a failed one keeps the button pressable).
  const dirtyRef = useRef(false)
  const [dirty, setDirty] = useState(false)
  const dispatch = useCallback((action: WriterAction): void => {
    dirtyRef.current = true
    setDirty(true)
    rawDispatch(action)
  }, [])
  const draftRef = useRef(draft)
  draftRef.current = draft

  // ── the local draft ──────────────────────────────────────────────────────
  const [loaded, setLoaded] = useState(false)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  // When this browser last stored the draft (epoch ms): set by a save only, so it is never in the
  // server HTML.
  const [savedAt, setSavedAt] = useState<number | null>(null)
  // Save draft reads "Saved" for `SAVED_FLASH_MS` after a press.
  const [justSaved, setJustSaved] = useState(false)
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const postedRef = useRef(false)
  // The bar over a draft loaded from this browser ("Draft restored … · saved 3 h ago"), and its
  // inline Discard confirmation; null once discarded (or when nothing was stored).
  const [restored, setRestored] = useState<string | null>(null)
  // The phone's "where is my draft" line, opened from the status in the top bar.
  const [phoneWhere, setPhoneWhere] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const keepRef = useRef<HTMLButtonElement>(null)
  const titleRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!localDraft) {
      setLoaded(true)
      return
    }
    const stored = draftStorage.load()
    if (stored && !isBlankDraft(stored)) {
      const next = applyPreset(stored, preset, categories)
      rawDispatch({ type: 'load', draft: next })
      // A new preset merged into the stored draft (another product's "Ask owners") is a change
      // storage does not hold yet: Save draft opens and autosave writes it.
      if (next !== stored) {
        dirtyRef.current = true
        setDirty(true)
      }
      setRestored(restoredDraftLine(stored.savedAt ?? null, Date.now()))
    }
    setLoaded(true)
    // The stored draft is read once, on arrival; the preset is the URL's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveNow = useCallback((): boolean => {
    if (!localDraft || postedRef.current) {
      return false
    }
    const now = Date.now()
    if (!draftStorage.save(draftRef.current, undefined, now)) {
      setSaveState('failed')
      return false
    }
    dirtyRef.current = false
    setDirty(false)
    setSavedAt(now)
    setSaveState('saved')
    return true
  }, [localDraft])

  const onSaveClick = (): void => {
    if (!saveNow()) {
      return
    }
    setJustSaved(true)
    if (flashTimer.current) {
      clearTimeout(flashTimer.current)
    }
    flashTimer.current = setTimeout(() => setJustSaved(false), SAVED_FLASH_MS)
  }

  useEffect(
    () => () => {
      if (flashTimer.current) {
        clearTimeout(flashTimer.current)
      }
    },
    []
  )

  useEffect(() => {
    if (!localDraft || !loaded || !dirtyRef.current || postedRef.current) {
      return undefined
    }
    const timer = setTimeout(saveNow, AUTOSAVE_MS)
    // Backgrounding the tab right after typing (iOS may discard it) writes the change now.
    const flush = (e: Event): void => {
      const now = flushDraftOnHide({
        event: e.type === 'pagehide' ? 'pagehide' : 'visibilitychange',
        visibilityState: document.visibilityState,
        localDraft,
        loaded,
        dirty: dirtyRef.current,
        posted: postedRef.current,
      })
      if (now) {
        clearTimeout(timer)
        saveNow()
      }
    }
    document.addEventListener('visibilitychange', flush)
    window.addEventListener('pagehide', flush)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', flush)
      window.removeEventListener('pagehide', flush)
    }
  }, [draft, loaded, saveNow, localDraft])

  const onBlurSave = (): void => {
    if (localDraft && loaded && dirtyRef.current) {
      saveNow()
    }
  }

  // ── photos ───────────────────────────────────────────────────────────────
  const files = useRef(new Map<string, File>())
  const uploads = useRef(new Map<string, MediaUpload>())
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const previewsRef = useRef(previews)
  previewsRef.current = previews
  const [notices, setNotices] = useState<Record<number, string>>({})

  const dropPreview = (key: string): void => {
    const url = previewsRef.current[key]
    if (url) {
      URL.revokeObjectURL(url)
      setPreviews(p => {
        const next = { ...p }
        delete next[key]
        return next
      })
    }
  }

  useEffect(
    () => () => {
      uploads.current.forEach(u => u.abort())
      Object.values(previewsRef.current).forEach(url => URL.revokeObjectURL(url))
    },
    []
  )

  const addFiles = (blockId: number, picked: File[]): void => {
    const skipped = { type: 0, size: 0, room: 0 }
    let room = imageRoom(draftRef.current, blockId)
    const images: DraftImage[] = []
    const shown: Record<string, string> = {}
    for (const file of picked) {
      const refusal = fileRefusal(file)
      if (refusal) {
        skipped[refusal] += 1
      } else if (room <= 0) {
        skipped.room += 1
      } else {
        room -= 1
        const key = newImageKey()
        files.current.set(key, file)
        shown[key] = URL.createObjectURL(file)
        images.push({
          key,
          status: 'queued',
          progress: 0,
          media_id: null,
          url: null,
          width: 0,
          height: 0,
          error: null,
          uploaded_at: null,
        })
      }
    }
    setNotices(n => ({ ...n, [blockId]: skippedFilesLine(skipped) }))
    if (!images.length) {
      return
    }
    setPreviews(p => ({ ...p, ...shown }))
    dispatch({ type: 'addImages', id: blockId, images })
    if (!usable(credentialRef.current, rejectedTokenRef.current)) {
      void showLogIn()
    }
  }

  const removeImage = (blockId: number, key: string): void => {
    uploads.current.get(key)?.abort()
    uploads.current.delete(key)
    files.current.delete(key)
    dropPreview(key)
    dispatch({ type: 'removeImage', id: blockId, key })
  }

  const retryImage = (blockId: number, key: string): void => {
    if (!files.current.has(key)) {
      // The file is gone (a reload): the tile cannot be sent again.
      removeImage(blockId, key)
      return
    }
    dispatch({
      type: 'updateImage',
      id: blockId,
      key,
      patch: { status: 'queued', progress: 0, error: null },
    })
    if (!usable(credentialRef.current, rejectedTokenRef.current)) {
      void showLogIn()
    }
  }

  const removeBlock = (block: DraftBlock): void => {
    if (block.type === 'images') {
      block.images.forEach(im => {
        uploads.current.get(im.key)?.abort()
        uploads.current.delete(im.key)
        files.current.delete(im.key)
        dropPreview(im.key)
      })
    }
    dispatch({ type: 'removeBlock', id: block.id })
  }

  // The queue: queued tiles start once there is a session and a free slot.
  useEffect(() => {
    const cred = credentialRef.current
    if (!token || !usable(cred, rejectedTokenRef.current)) {
      return
    }
    const queued: { blockId: number; im: DraftImage }[] = []
    for (const b of draft.blocks) {
      if (b.type === 'images') {
        b.images.forEach(im => im.status === 'queued' && queued.push({ blockId: b.id, im }))
      }
    }
    for (const { blockId, im } of queued) {
      if (uploads.current.size >= UPLOAD_SLOTS) {
        break
      }
      const file = files.current.get(im.key)
      if (!file) {
        dispatch({
          type: 'updateImage',
          id: blockId,
          key: im.key,
          patch: { status: 'error', error: 'Pick this photo again' },
        })
        continue
      }
      const key = im.key
      const update = (patch: Partial<DraftImage>): void => {
        const id = imageBlockId(draftRef.current, key)
        if (id !== null) {
          dispatch({ type: 'updateImage', id, key, patch })
        }
      }
      update({ status: 'uploading', progress: 0, error: null })
      const upload = discussionsApi.uploadMedia(cred, file, progress => update({ progress }))
      uploads.current.set(key, upload)
      upload.promise.then(
        media => {
          uploads.current.delete(key)
          update({
            status: 'done',
            progress: 1,
            media_id: media.id,
            url: media.url,
            width: media.width,
            height: media.height,
            uploaded_at: Date.now(),
          })
        },
        (e: unknown) => {
          uploads.current.delete(key)
          if (e instanceof OwnerApiError && e.code === 'aborted') {
            return
          }
          if (isSignInError(e)) {
            rejectedTokenRef.current = cred.token
            update({ status: 'queued', progress: 0 })
            void showLogIn()
            return
          }
          update({
            status: 'error',
            error: e instanceof OwnerApiError ? e.message : 'Upload failed',
          })
        }
      )
    }
  }, [draft, token, showLogIn])

  // ── posting ──────────────────────────────────────────────────────────────
  const [posting, setPosting] = useState(false)
  const [wantPost, setWantPost] = useState(false)
  const [postError, setPostError] = useState<string | null>(null)
  // Editing: gear locks up front when the post has answers, or once DealHunt says `gear_locked`.
  const [gearLocked, setGearLocked] = useState(edit?.gearLocked ?? false)

  const pending = uploadsPending(draft)
  const patch = useMemo(
    () => (edit ? toPatchBody(edit.initial, draft, { gearLocked }) : null),
    [edit, draft, gearLocked]
  )
  const unchanged = patch !== null && isEmptyPatch(patch)
  // Editing: leaving with changes not yet saved asks first (the edit view keeps no local draft).
  // Off once a save went through — its own navigation must never be stopped.
  const guard = useLeaveGuard(Boolean(edit) && editHasChanges(patch, draft), router, postedRef)
  const postable = canPost(draft) && !pending && !posting && !unchanged

  /** Editing: PATCH only what changed, then back to the thread. The shared `/me` is forgotten
   *  before and after by `keyedSend` (Directive 0d81c597). */
  const saveEdit = async (
    target: WriterEdit,
    cred: OwnerCredential,
    current: WriterDraft
  ): Promise<void> => {
    const body = toPatchBody(target.initial, current, { gearLocked })
    if (isEmptyPatch(body)) {
      setPosting(false)
      return
    }
    try {
      const answer = await discussionsApi.update(cred, target.id, body)
      postedRef.current = true
      // The thread says "Your changes are saved." once (its HTML is shared-cached).
      savedNoticeStorage.write({ id: target.id })
      const trailingSlash = publicConfig('useTrailingSlash')
      const to = postedRedirect(answer?.discussion?.url, trailingSlash)
      await router.push(to === discussionsHref(trailingSlash) ? target.threadHref : to)
    } catch (e) {
      if (isSignInError(e)) {
        rejectedTokenRef.current = cred.token
        setWantPost(true)
        void showLogIn()
      } else if (e instanceof OwnerApiError && e.code === 'gear_locked') {
        // Answered while the editor was open: the gear goes back to the post's and locks; the
        // rest of the edit stays for the next Save.
        setGearLocked(true)
        rawDispatch({ type: 'load', draft: { ...draftRef.current, gear: target.initial.gear } })
        setPostError(`${e.message} Your other changes are still here — save again.`)
      } else {
        setPostError(
          e instanceof OwnerApiError ? e.message : 'Something went wrong — try again in a moment.'
        )
      }
    } finally {
      setPosting(false)
    }
  }

  const post = async (): Promise<void> => {
    const current = draftRef.current
    if (!canPost(current) || uploadsPending(current) || posting) {
      return
    }
    // A tab open past DealHunt's 24 h GC: drop the photos it may have collected, say which, and
    // let the reader post again — never send a media id that would refuse the whole post.
    const expired = expiredPhotos(current, Date.now())
    if (expired.length) {
      const byBlock = new Map<number, number[]>()
      for (const photo of expired) {
        removeImage(photo.blockId, photo.key)
        byBlock.set(photo.blockId, [...(byBlock.get(photo.blockId) ?? []), photo.position])
      }
      setNotices(n => {
        const next = { ...n }
        byBlock.forEach((positions, blockId) => {
          next[blockId] = expiredPhotosLine(positions)
        })
        return next
      })
      setPostError(
        `${
          expired.length === 1 ? 'A photo was' : `${expired.length} photos were`
        } uploaded over 23 hours ago and removed — see the photo sets, then post again.`
      )
      return
    }
    const cred = credentialRef.current
    if (!usable(cred, rejectedTokenRef.current)) {
      saveNow()
      setWantPost(true)
      void showLogIn()
      return
    }
    setPosting(true)
    setPostError(null)
    if (edit) {
      await saveEdit(edit, cred, current)
      return
    }
    try {
      const answer = await discussionsApi.create(cred, toCreateBody(current))
      postedRef.current = true
      // A new post only (an edit returned above): its kind, the linked gear, the first product.
      const product = current.gear.find(g => g.kind === 'product')
      track('community_page_owner_discussion_posted', {
        page_type: 'community_page',
        location: 'writer',
        kind: current.kind,
        item_count: current.gear.length,
        target_product_id: product?.kind === 'product' ? product.node_id : null,
        target_product_name: product?.name ?? null,
      })
      draftStorage.clear()
      await router.push(postedRedirect(answer?.url, publicConfig('useTrailingSlash')))
    } catch (e) {
      if (isSignInError(e)) {
        rejectedTokenRef.current = cred.token
        setWantPost(true)
        void showLogIn()
      } else {
        setPostError(
          e instanceof OwnerApiError ? e.message : 'Something went wrong — try again in a moment.'
        )
      }
    } finally {
      setPosting(false)
    }
  }

  // Signed in from the dialog after pressing Post: post now.
  const postRef = useRef(post)
  postRef.current = post
  useEffect(() => {
    if (wantPost && token && token !== rejectedTokenRef.current) {
      setWantPost(false)
      void postRef.current()
    }
  }, [wantPost, token])

  // ── discarding the restored draft ────────────────────────────────────────
  useEffect(() => {
    if (confirmDiscard) {
      keepRef.current?.focus()
    }
  }, [confirmDiscard])

  /** Back to a new post: the stored draft, its photos (uploads aborted, previews released) and
   *  every line about them are gone, and the link's preset applies to the fresh draft. */
  const discard = (): void => {
    if (posting) {
      return
    }
    uploads.current.forEach(u => u.abort())
    uploads.current.clear()
    files.current.clear()
    Object.values(previewsRef.current).forEach(url => URL.revokeObjectURL(url))
    setPreviews({})
    setNotices({})
    setPostError(null)
    setWantPost(false)
    draftStorage.clear()
    dirtyRef.current = false
    setDirty(false)
    setSavedAt(null)
    setSaveState('idle')
    setJustSaved(false)
    rawDispatch({ type: 'load', draft: freshDraft(preset, categories) })
    setRestored(null)
    setConfirmDiscard(false)
    titleRef.current?.focus()
  }

  // ── ✕ on a phone (new post) ──────────────────────────────────────────────
  // The draft is written first, so leaving loses nothing; only a browser that refuses to store it
  // asks before a non-blank draft goes (the edit view's ✕ is a link: `useLeaveGuard` asks there).
  const [closeAsk, setCloseAsk] = useState(false)
  const leaveWriter = (): void => {
    if (!router) {
      return
    }
    const initial = (window as { __NEXT_DATA__?: { page?: unknown } }).__NEXT_DATA__?.page
    const back = closeGoesBack({
      initialPage: typeof initial === 'string' ? initial : null,
      currentPage: router.pathname ?? null,
      referrer: document.referrer,
      origin: window.location.origin,
      historyLength: window.history.length,
    })
    if (back) {
      router.back()
    } else {
      void router.push(discussionsHref(publicConfig('useTrailingSlash')))
    }
  }
  const onClose = (): void => {
    if (posting) {
      return
    }
    const blank = isBlankDraft(draftRef.current)
    // A restored draft the reader emptied: the stored one goes too, never comes back on return.
    if (dirtyRef.current && blank) {
      draftStorage.clear()
    }
    const kept = !dirtyRef.current || blank || saveNow()
    if (kept) {
      leaveWriter()
    } else {
      setCloseAsk(true)
    }
  }
  const closeGuard = useMemo(
    () => ({
      leave: closeAsk ? ({ kind: 'back' } as const) : null,
      keep: () => setCloseAsk(false),
      discard: () => {
        setCloseAsk(false)
        postedRef.current = true
        leaveWriter()
      },
    }),
    // leaveWriter reads the router and the history only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeAsk, router]
  )

  // ── the title grows with its text on a phone (desktop keeps rows={2}) ─────
  useEffect(() => {
    const el = titleRef.current
    if (!el || typeof window.matchMedia !== 'function') {
      return undefined
    }
    const query = window.matchMedia(WRITER_PHONE_QUERY)
    const fit = (): void => {
      if (query.matches) {
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      } else {
        el.style.height = ''
      }
    }
    fit()
    // A rotation that stays under the breakpoint, or a late webfont, changes the lines too.
    window.addEventListener('resize', fit)
    query.addEventListener?.('change', fit)
    return () => {
      window.removeEventListener('resize', fit)
      query.removeEventListener?.('change', fit)
    }
  }, [draft.title])

  // ── the phone toolbar and reorder mode ──────────────────────────────────
  // Typing at the end of a field that runs under the fixed toolbar: scroll the line clear of it
  // (the browser only keeps the caret inside the viewport, which the bar overlaps). Phone only:
  // the bar is display:none above 767.98px.
  const keepCaretClear = (e: React.FormEvent<HTMLDivElement>): void => {
    const field = e.target
    if (!(field instanceof HTMLTextAreaElement) && !(field instanceof HTMLInputElement)) {
      return
    }
    const bar = document.querySelector<HTMLElement>(`.${styles.toolbar}`)
    if (!bar || window.getComputedStyle(bar).display === 'none') {
      return
    }
    let caretAtEnd = false
    try {
      caretAtEnd = field.selectionEnd === field.value.length
    } catch {
      // An input type without a selection API (none in the writer today).
    }
    const by = caretUnderBar({
      fieldBottom: field.getBoundingClientRect().bottom,
      barTop: bar.getBoundingClientRect().top,
      caretAtEnd,
    })
    if (by > 0) {
      window.scrollBy(0, by)
    }
  }
  const insets = useViewportInsets()
  const [reordering, setReordering] = useState(false)
  // The block a toolbar press added: its field takes focus once it renders.
  const [focusBlock, setFocusBlock] = useState<number | null>(null)
  const blocksRef = useRef<HTMLOListElement>(null)
  useEffect(() => {
    if (focusBlock === null) {
      return
    }
    setFocusBlock(null)
    const row = blocksRef.current?.querySelector<HTMLElement>(`[data-block-id="${focusBlock}"]`)
    row?.scrollIntoView({ block: 'center' })
    row
      ?.querySelector<HTMLElement>('input:not([type="file"]), textarea')
      ?.focus({ preventScroll: true })
  }, [focusBlock])
  // Reorder mode is the phone's: leaving the phone layout (a rotation, a resize) ends it.
  useEffect(() => {
    if (!reordering || typeof window.matchMedia !== 'function') {
      return undefined
    }
    const query = window.matchMedia(WRITER_PHONE_QUERY)
    const onChange = (): void => {
      if (!query.matches) {
        setReordering(false)
      }
    }
    onChange()
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [reordering])
  /** A toolbar press: the block, with an empty text block after it so writing carries on below
   *  (text is the default block — no button; `toCreateBody` drops an empty one). When the post
   *  already ends on an empty text block, the new block goes in before it instead of adding
   *  another — no stacked empty paragraphs, one block of DealHunt's 30 per press. */
  const addFromToolbar = (type: Exclude<DraftBlockType, 'text'>): void => {
    const current = draftRef.current
    if (!canAddBlock(current)) {
      return
    }
    const id = current.nextId
    const last = current.blocks[current.blocks.length - 1]
    setFocusBlock(id)
    dispatch({ type: 'addBlock', block: type })
    if (last?.type === 'text' && !last.body.trim()) {
      dispatch({ type: 'moveBlockTo', id, index: current.blocks.length - 1 })
    } else {
      dispatch({ type: 'addBlock', block: 'text' })
    }
  }

  // ── render ───────────────────────────────────────────────────────────────
  const copy = kindCopy(draft.kind)
  const list = useMemo(() => checklist(draft), [draft])
  const addBlock = (type: DraftBlockType): void => dispatch({ type: 'addBlock', block: type })
  const onAddGear = useCallback(
    (gear: DraftGear) =>
      dispatch({
        type: 'addGear',
        gear,
        categoryId: categoryFromGear([gear], pills),
      }),
    [pills]
  )
  const onRemoveGear = useCallback((key: string) => dispatch({ type: 'removeGear', key }), [])
  const saved = writerSavedStatus({ edit: Boolean(edit), saveState, savedAt })
  // "Find it in your profile": /profile#draft, which forwards a member to their hub's card.
  const draftHome = draftHomeHref(publicConfig('useTrailingSlash'))
  let postLabel = posting ? 'Posting…' : copy.postLabel
  // The phone's top bar is narrow: "Post" / "Save" (the status line says "Posting…").
  const phonePostLabel = edit ? 'Save' : 'Post'
  if (edit) {
    postLabel = posting ? 'Saving…' : 'Save changes'
  }
  const phoneStatus = phoneDraftStatus({
    edit: Boolean(edit),
    loaded,
    dirty,
    saveState,
    posting,
    restored: restored !== null,
    unchanged,
  })
  // Phone: the status explains where the draft is kept — a new post with something saved (or
  // being saved), never a failed save (nothing is kept) nor the edit view (no local draft).
  const phoneWhereOffered = localDraft && phoneStatus !== '' && saveState !== 'failed' && !posting
  const toolbarReason = postDisabledReason(draft, { pending, posting, unchanged })
  let postHint: string | null = postBlocker(draft)
  if (!postHint && pending) {
    postHint = 'Photos are still uploading.'
  }

  return (
    <div
      className={styles.writer}
      onBlur={onBlurSave}
      onInput={e => {
        // Typing again closes the phone's where-line (it hangs over the kind toggle).
        setPhoneWhere(false)
        keepCaretClear(e)
      }}
      style={
        insets.top > 0
          ? ({ '--writer-vv-top': `${insets.top}px` } as React.CSSProperties)
          : undefined
      }
    >
      {edit && <LeaveDialog guard={guard} />}
      {localDraft && closeAsk && <LeaveDialog guard={closeGuard} />}
      <div className={styles.bar}>
        <Crumbs items={crumbs} />
        <div className={styles.actions}>
          {/* Phone only (M1c): ✕, then the draft status; hidden above 767.98px. */}
          {edit ? (
            <Link href={edit.threadHref} className={styles.close} aria-label="Close">
              <span aria-hidden="true">{'✕'}</span>
            </Link>
          ) : (
            <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">{'✕'}</span>
            </button>
          )}
          {/* A new post's status is a button: a tap says where the draft is kept (the bar has
              no room for the sentence). One element while the text changes, so focus stays. */}
          {phoneWhereOffered ? (
            <button
              type="button"
              className={clsx(styles.phoneStatus, styles.phoneStatusButton)}
              aria-expanded={phoneWhere}
              aria-controls="writer-draft-where"
              onClick={() => setPhoneWhere(open => !open)}
            >
              <span aria-live="polite">{phoneStatus}</span>
            </button>
          ) : (
            <span className={styles.phoneStatus} aria-live="polite">
              {phoneStatus}
            </span>
          )}
          {/* role="status" is aria-live="polite" (and atomic): the whole line is read on a save. */}
          {localDraft ? (
            <>
              <span className={styles.status} role="status">
                {saved?.line}
                {saved?.time && <span className={styles.statusTime}>{` · ${saved.time}`}</span>}
                {saved?.findIt && (
                  <>
                    {' · '}
                    <Link href={draftHome} className={styles.statusLink}>
                      {DRAFT_COPY.findIt}
                    </Link>
                  </>
                )}
              </span>
              <button
                type="button"
                className={styles.secondary}
                disabled={!loaded || !dirty}
                onClick={onSaveClick}
              >
                {justSaved && !dirty ? 'Saved' : 'Save draft'}
              </button>
            </>
          ) : (
            edit && (
              <Link href={edit.threadHref} className={clsx(styles.secondary, styles.cancel)}>
                {'Cancel'}
              </Link>
            )
          )}
          {reordering && (
            <button
              type="button"
              className={clsx(base.btn, styles.done)}
              onClick={() => setReordering(false)}
            >
              {'Done'}
            </button>
          )}
          <button
            type="button"
            className={clsx(base.btn, styles.post, { [styles.postAway]: reordering })}
            disabled={!postable}
            aria-describedby={postHint ? 'writer-post-hint' : undefined}
            onClick={() => void post()}
          >
            <span className={styles.wideLabel}>{postLabel}</span>
            <span className={styles.phoneLabel}>{phonePostLabel}</span>
          </button>
        </div>
        {phoneWhereOffered && phoneWhere && (
          <p id="writer-draft-where" className={styles.phoneWhere}>
            {`${DRAFT_COPY.deviceNote} `}
            <Link href={draftHome} className={styles.statusLink}>
              {DRAFT_COPY.findIt}
            </Link>
          </p>
        )}
      </div>
      {(postHint || postError) && (
        <p
          id="writer-post-hint"
          className={clsx(styles.postHint, postError ? styles.postError : styles.postHintWide)}
          role={postError ? 'alert' : undefined}
        >
          {postError ?? postHint}
        </p>
      )}
      {localDraft && restored && (
        <div className={styles.restored}>
          <span>{restored}</span>
          {confirmDiscard ? (
            <span className={styles.restoredConfirm} role="group" aria-label="Discard draft">
              <span>{'Discard this draft? It cannot be undone.'}</span>
              <button type="button" className={styles.restoredDanger} onClick={discard}>
                {'Discard'}
              </button>
              <button
                ref={keepRef}
                type="button"
                className={styles.restoredButton}
                onClick={() => setConfirmDiscard(false)}
              >
                {'Keep it'}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={styles.restoredButton}
              disabled={posting}
              onClick={() => setConfirmDiscard(true)}
            >
              {'Discard draft'}
            </button>
          )}
        </div>
      )}

      <div className={styles.columns}>
        <div className={styles.main}>
          {edit ? (
            // PATCH takes no kind: the post's kind shows, fixed.
            <div className={styles.kinds}>
              <span className={clsx(styles.kind, styles.kindOn, styles.kindFixed)}>
                {copy.label}
              </span>
            </div>
          ) : (
            <div className={styles.kinds} role="group" aria-label="Kind of post">
              {DISCUSSION_KINDS.map(kind => (
                <button
                  key={kind}
                  type="button"
                  className={clsx(styles.kind, { [styles.kindOn]: draft.kind === kind })}
                  aria-pressed={draft.kind === kind}
                  onClick={() => dispatch({ type: 'setKind', kind })}
                >
                  {kindCopy(kind).label}
                </button>
              ))}
            </div>
          )}
          <p className={styles.kindHint}>{edit ? KIND_FIXED_LINE : copy.hint}</p>
          <textarea
            ref={titleRef}
            className={styles.title}
            value={draft.title}
            rows={2}
            maxLength={DISCUSSION_LIMITS.titleChars}
            placeholder={copy.titlePlaceholder}
            aria-label="Title"
            onChange={e => dispatch({ type: 'setTitle', title: e.target.value })}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
          />
          <PhoneChecklist checklist={list} />
          <div className={styles.rule} />

          {reordering && (
            <ReorderList
              blocks={draft.blocks}
              onMoveTo={(id, index) => dispatch({ type: 'moveBlockTo', id, index })}
              onRemove={removeBlock}
            />
          )}
          <ol ref={blocksRef} className={clsx(styles.blocks, { [styles.blocksAway]: reordering })}>
            {draft.blocks.map((block, i) => (
              <li key={block.id} className={styles.block} data-block-id={block.id}>
                <div className={styles.blockBody}>
                  {block.type === 'text' && (
                    <TextBlock
                      body={block.body}
                      onChange={value => dispatch({ type: 'setText', id: block.id, value })}
                    />
                  )}
                  {block.type === 'heading' && (
                    <HeadingBlock
                      text={block.text}
                      onChange={value => dispatch({ type: 'setText', id: block.id, value })}
                    />
                  )}
                  {block.type === 'images' && (
                    <ImagesBlock
                      images={block.images}
                      caption={block.caption}
                      previews={previews}
                      coverKey={isCover(draft, block.id, 0) ? block.images[0]?.key ?? null : null}
                      room={imageRoom(draft, block.id)}
                      notice={notices[block.id] || null}
                      onFiles={picked => addFiles(block.id, picked)}
                      onRemove={key => removeImage(block.id, key)}
                      onRetry={key => retryImage(block.id, key)}
                      onCaption={value => dispatch({ type: 'setCaption', id: block.id, value })}
                    />
                  )}
                  {block.type === 'link' && (
                    <LinkBlock
                      url={block.url}
                      onChange={value => dispatch({ type: 'setUrl', id: block.id, value })}
                    />
                  )}
                </div>
                <div className={styles.blockTools}>
                  <button
                    type="button"
                    className={styles.tool}
                    aria-label="Move up"
                    title="Move up"
                    disabled={i === 0}
                    onClick={() => dispatch({ type: 'moveBlock', id: block.id, delta: -1 })}
                  >
                    {'▲'}
                  </button>
                  <button
                    type="button"
                    className={styles.tool}
                    aria-label="Move down"
                    title="Move down"
                    disabled={i === draft.blocks.length - 1}
                    onClick={() => dispatch({ type: 'moveBlock', id: block.id, delta: 1 })}
                  >
                    {'▼'}
                  </button>
                  <button
                    type="button"
                    className={clsx(styles.tool, styles.toolRemove)}
                    aria-label="Remove block"
                    title="Remove"
                    onClick={() => removeBlock(block)}
                  >
                    {'✕'}
                  </button>
                </div>
              </li>
            ))}
          </ol>

          <PhonePickers
            categories={categories}
            showCategory={pills.length > 0}
            selected={draft.categoryId}
            onSelect={id => dispatch({ type: 'setCategory', id })}
            gear={draft.gear}
            onAddGear={onAddGear}
            onRemoveGear={onRemoveGear}
            gearLocked={gearLocked}
          />
          <PhoneToolbar
            inset={insets.bottom}
            canAdd={canAddBlock(draft)}
            canReorder={draft.blocks.length > 1}
            reordering={reordering}
            reason={reordering ? null : toolbarReason}
            onAdd={addFromToolbar}
            onReorder={() => setReordering(on => !on)}
          />
          <div className={styles.add} role="group" aria-label="Add a block">
            <span className={styles.addLabel} aria-hidden="true">
              {'Add'}
            </span>
            {ADD_BUTTONS.map(button => (
              <button
                key={button.type}
                type="button"
                className={styles.addButton}
                disabled={!canAddBlock(draft)}
                onClick={() => addBlock(button.type)}
              >
                <span aria-hidden="true">{button.glyph}</span> {button.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.side}>
          <GearPanel
            gear={draft.gear}
            onAdd={onAddGear}
            onRemove={onRemoveGear}
            locked={gearLocked}
          />
          {pills.length > 0 && (
            <CategoryPanel
              categories={categories}
              selected={draft.categoryId}
              onSelect={id => dispatch({ type: 'setCategory', id })}
            />
          )}
          <Checklist checklist={list} />
        </div>
      </div>
    </div>
  )
}

export default Writer
